import type { ReactNode } from 'react';

import classNames from 'classnames';

import { currencyFormatter } from '../../utils/currencyFormatter';

import type { Price, PricingFormMode, Tier } from '../../types';
import * as styles from '../Pricing.css';
import { getPriceDisplayOptions } from './getPriceDisplayOptions';

interface PriceFiguresProps {
  selectedPrice?: Price;
  tier: Tier;
  currentFormMode?: PricingFormMode;
  isInternalCheckout: boolean;
  showWeeklyPricing?: boolean;
  translations?: Record<string, string>;
  inlineDiscount?: boolean;
  align?: 'start' | 'center';
  donationSlot: ReactNode;
  suffixFallback?: string;
}

/**
 * The price figures a pricing surface shows for the selected price: main figure,
 * discount strikethrough, suffix, weekly footnote, and the paid-trial copy.
 */
export const PriceFigures = ({
  selectedPrice,
  tier,
  currentFormMode,
  isInternalCheckout,
  showWeeklyPricing = false,
  translations,
  inlineDiscount = false,
  align = 'start',
  donationSlot,
  suffixFallback,
}: PriceFiguresProps) => {
  const isGroupMode = currentFormMode === 'group';
  const perSeatPrefix = isGroupMode ? translations?.['upgrade.tabs.group.per_seat_prefix'] : undefined;

  const {
    displayCents,
    showDonationAmountInput,
    billedAsCents,
    isWeeklyDisplay,
    isPaidTrial,
    paidTrialPriceCents,
    paidTrialInlineSuffix,
    paidTrialAfterText,
    strikethroughCents,
  } = getPriceDisplayOptions({ selectedPrice, tier, isGroupMode, isInternalCheckout, showWeeklyPricing });

  const currency = selectedPrice?.currency || 'USD';
  const intervalText = selectedPrice?.denominator || selectedPrice?.interval.split('_').join(' ');

  const suffixText = isWeeklyDisplay
    ? translations?.['upgrade.weekly.per_week'] || '/week*'
    : selectedPrice?.denominator
      ? perSeatPrefix
        ? `${perSeatPrefix}/${selectedPrice.denominator}`
        : `/${selectedPrice.denominator}`
      : showDonationAmountInput
        ? undefined
        : suffixFallback;

  return (
    <>
      <div
        className={classNames(
          'flex flex-row flex-wrap font-semibold',
          inlineDiscount ? 'gap-2' : 'gap-1',
          align === 'center' ? 'items-baseline justify-center' : 'items-center',
        )}
      >
        {isPaidTrial && paidTrialPriceCents != null ? (
          <div className={classNames('flex flex-row items-baseline gap-1', align === 'start' && 'w-full')}>
            <p className={classNames('leading-none', styles.tierPrice)}>
              {currencyFormatter({ value: paidTrialPriceCents / 100, currency })}
            </p>
            {paidTrialInlineSuffix && (
              <p className={classNames('leading-none', styles.tierPriceDenominator)}>{paidTrialInlineSuffix}</p>
            )}
          </div>
        ) : (
          <>
            {strikethroughCents != null && selectedPrice && (
              <p className={classNames('line-through leading-none', styles.tierOfferPrice)}>
                {currencyFormatter({ value: strikethroughCents / 100, currency })}
              </p>
            )}
            <div
              className={classNames('flex flex-row items-baseline gap-1', {
                'w-full': !inlineDiscount && align === 'start',
              })}
            >
              {!showDonationAmountInput ? (
                <p className={classNames('leading-none', styles.tierPrice)}>
                  {currencyFormatter({ value: (displayCents ?? 0) / 100, currency })}
                </p>
              ) : (
                donationSlot
              )}
              {suffixText && <p className={classNames('leading-none', styles.tierPriceDenominator)}>{suffixText}</p>}
            </div>
            {isWeeklyDisplay && selectedPrice && (
              <p className={classNames('text-sm leading-normal font-normal', styles.tierPriceDenominator)}>
                {translations?.['upgrade.weekly.billed_as']
                  ?.replace('{{price}}', currencyFormatter({ value: (billedAsCents ?? 0) / 100, currency }))
                  ?.replace('{{interval}}', intervalText ?? '') ||
                  `*Billed as ${currencyFormatter({ value: (billedAsCents ?? 0) / 100, currency })} per ${intervalText}`}
              </p>
            )}
          </>
        )}
      </div>
      {isPaidTrial && paidTrialAfterText && (
        <p className={classNames('text-sm leading-normal font-normal', styles.tierOfferDetailsTextColor)}>
          {paidTrialAfterText}
        </p>
      )}
    </>
  );
};
